'use client'

import {
  PropsWithChildren,
  ReactNode,
  Suspense,
  useEffect,
  useState,
} from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { SessionProvider } from 'next-auth/react'
import type { Session } from 'next-auth'
import { ApiClientStoreProvider } from '@/lib/apis/clients/ApiClientStore'
import { isHttpError } from '@/lib/apis/error'
import TreatmentQuizAttributionCapture from '@/features/quiz/treatment-quiz-attribution-capture'
import ProviderSessionActivity from '@/shared/ProviderSessionActivity'

type Props = {
  children: ReactNode
  session: Session | null
}

export default function ClientProviders({ children, session }: Props) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: (failureCount, error) =>
              failureCount < 3 &&
              (!isHttpError(error) || error.status !== 401),
          },
        },
      }),
  )

  return (
    <PostHogProvider>
      <SessionProvider session={session}>
        <ApiClientStoreProvider>
          <QueryClientProvider client={client}>
            <ProviderSessionActivity />
            <Suspense fallback={null}>
              <TreatmentQuizAttributionCapture />
            </Suspense>
            {children}
          </QueryClientProvider>
        </ApiClientStoreProvider>
      </SessionProvider>
    </PostHogProvider>
  )
}

function PostHogProvider({ children }: PropsWithChildren<{}>) {
  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: 'https://f.nextclinic.com.au',
      defaults: '2026-01-30',
      person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
    })
  }, [])

  return <PHProvider client={posthog}>{children}</PHProvider>
}
